// src/router.tsx
// SPA route definitions — maps the Next.js App Router pages to React Router v7
// with nested layout routes replicating the original layout hierarchy:
//   PublicLayout (AnimatedBackground) > AuthLayout (glassmorphism card) > page
//   `/` per landing mode: see src/landingRoutes.tsx
//   ProtectedRoute > page
//   AdminRoute > page

import { createBrowserRouter, Navigate } from 'react-router'
import { lazy, Suspense } from 'react'
import { ProtectedRoute } from './guards/ProtectedRoute'
import { AdminRoute } from './guards/AdminRoute'
import { EditorErrorBoundary } from './components/EditorErrorBoundary'

// Layout routes (eager — they wrap all children)
import AuthLayout from './layouts/AuthLayout'

// What `/` serves, and whether it sits inside PublicLayout, is a one-line
// switch: src/lib/landing-mode.ts (see docs/landing-modes.md).
import { publicRoutes } from './landingRoutes'
import { LANDING_MODE } from '@/src/lib/landing-mode'

// Lazy-load everything else
const LoginPage = lazy(() => import('@/src/pages/auth/LoginPage'))
const SignupPage = lazy(() => import('@/src/pages/auth/SignupPage'))
const ResetPasswordPage = lazy(
  () => import('@/src/pages/auth/ResetPasswordPage'),
)
const AuthCallbackPage = lazy(() => import('@/src/routes/AuthCallback'))
const DashboardPage = lazy(() => import('@/src/pages/DashboardPage'))
const EditorPage = lazy(() => import('@/src/pages/editor/EditorPage'))
const AdminPage = lazy(() => import('@/src/pages/AdminPage'))
const PendingApprovalPage = lazy(
  () => import('@/src/pages/PendingApprovalPage'),
)

function LazyFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-black">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
    </div>
  )
}

function EditorLoadingFallback() {
  return (
    <div
      className="flex h-screen w-screen items-center justify-center bg-background"
      role="status"
      aria-label="Loading editor"
    >
      <div className="space-y-4 text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" />
        <p className="text-sm text-foreground-muted">Loading editor...</p>
      </div>
    </div>
  )
}

function withSuspense(
  Component: React.ComponentType,
  fallback?: React.ReactNode,
) {
  return (
    <Suspense fallback={fallback ?? <LazyFallback />}>
      <Component />
    </Suspense>
  )
}

export const router = createBrowserRouter([
  // Public routes — wrapped in PublicLayout (AnimatedBackground); `/` joins
  // them in the fallback modes and is its own top-level route in 'showcase'
  ...publicRoutes(LANDING_MODE, [
    // Auth routes — additionally wrapped in AuthLayout (nav + glassmorphism card)
    {
      element: <AuthLayout />,
      children: [
        {
          path: '/auth/login',
          element: withSuspense(LoginPage),
        },
        {
          path: '/auth/signup',
          element: withSuspense(SignupPage),
        },
        {
          path: '/auth/reset-password',
          element: withSuspense(ResetPasswordPage),
        },
      ],
    },
    {
      path: '/auth/callback',
      element: withSuspense(AuthCallbackPage),
    },
    {
      path: '/pending-approval',
      element: withSuspense(PendingApprovalPage),
    },
  ]),

  // Protected routes
  {
    path: '/dashboard',
    element: <ProtectedRoute>{withSuspense(DashboardPage)}</ProtectedRoute>,
  },
  {
    path: '/editor/:projectId',
    element: (
      <ProtectedRoute>
        <EditorErrorBoundary>
          {withSuspense(EditorPage, <EditorLoadingFallback />)}
        </EditorErrorBoundary>
      </ProtectedRoute>
    ),
  },

  // Admin routes
  {
    path: '/admin',
    element: <AdminRoute>{withSuspense(AdminPage)}</AdminRoute>,
  },

  // Catch-all redirect
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
])
